import React from "react";
import { Flex, Text, Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { Severity } from "../../../constants";
import { DataTypeChip } from "../DataTypeChip";
import { SeverityChip } from "../SeverityChip";
import { FileValidationData } from "../types";
import * as styles from "./styles_vanilla.css";

interface FileValidationCardProps {
  filesData: FileValidationData[];
  onDownloadErrors?: (fileName: string) => void;
  onDownloadMappedFile?: (fileName: string) => void;
  onReplaceFile?: (fileName: string, toolType: string) => void;
  isDownloadingErrors?: (fileName: string) => boolean;
  isDownloadingMappedFile?: (fileName: string) => boolean;
}

const FileValidationCard: React.FC<FileValidationCardProps> = ({
  filesData,
  onDownloadErrors,
  onDownloadMappedFile,
  onReplaceFile,
  isDownloadingErrors,
  isDownloadingMappedFile,
}) => {
  const renderFileCard = (fileData: FileValidationData) => (
    <Flex key={fileData.fileName} flexDirection="column" className={styles.cardContainer}>
      {/* File Header with chips at rightmost position */}
      <Flex alignItems="center" justifyContent="space-between" className={styles.fileHeader}>
        <Flex alignItems="center" gap={SPACING.MEDIUM}>
          <Icon type="file-alt" color={Colors.Neutral900} size="x-large" />
          <Flex alignItems="center" gap={SPACING.SMALL}>
            <Text variant="secondary-xl" className={styles.fileNameColor}>
              {fileData.fileName}
            </Text>
            <DataTypeChip dataType={fileData.dataType} />
          </Flex>
        </Flex>

        <Flex alignItems="center" gap={SPACING.SMALL}>
          {fileData.errorCount > 0 && (
            <SeverityChip severity={Severity.ERROR} count={fileData.errorCount} />
          )}
          {fileData.warningCount > 0 && (
            <SeverityChip severity={Severity.WARNING} count={fileData.warningCount} />
          )}
        </Flex>
      </Flex>

      {/* Summary Stats with buttons inline */}
      <Flex alignItems="center" justifyContent="space-between" className={styles.summaryStats}>
        <Flex gap={SPACING.LARGE}>
          <Flex flexDirection="column" alignItems="center">
            <Text variant="h3" className={styles.totalValueColor}>
              {fileData.totalRecords}
            </Text>
            <Text variant="regular-sm" className={styles.labelTextColor}>
              Total
            </Text>
          </Flex>
          <Flex flexDirection="column" alignItems="center">
            <Text variant="h3" className={styles.validValueColor}>
              {fileData.validRecords}
            </Text>
            <Text variant="regular-sm" className={styles.labelTextColor}>
              Valid
            </Text>
          </Flex>
          <Flex flexDirection="column" alignItems="center">
            <Text variant="h3" className={styles.errorValueColor}>
              {fileData.errorCount}
            </Text>
            <Text variant="regular-sm" className={styles.labelTextColor}>
              Errors
            </Text>
          </Flex>
        </Flex>

        <Flex gap={SPACING.MEDIUM}>
          <Button
            variant="neutral"
            icon="download"
            iconPosition="left"
            isLoading={isDownloadingErrors?.(fileData.fileName)}
            disabled={isDownloadingErrors?.(fileData.fileName)}
            onClick={() => onDownloadErrors?.(fileData.fileName)}
          >
            Download Errors
          </Button>
          <Button
            variant="neutral"
            icon="download"
            iconPosition="left"
            isLoading={isDownloadingMappedFile?.(fileData.fileName)}
            disabled={isDownloadingMappedFile?.(fileData.fileName)}
            onClick={() => onDownloadMappedFile?.(fileData.fileName)}
          >
            Download Mapped File
          </Button>
          <Button
            variant="neutral"
            icon="upload"
            iconPosition="left"
            onClick={() => onReplaceFile?.(fileData.fileName, fileData.toolType)}
          >
            Replace File
          </Button>
        </Flex>
      </Flex>

      {/* Issues Table */}
      {fileData.issues.length > 0 && (
        <Flex flexDirection="column" className={styles.issuesTable}>
          {/* Table Header */}
          <div className={styles.tableHeader}>
            <Text variant="strong-sm" color={Colors.Neutral700} className={styles.columnRow}>
              Row
            </Text>
            <Text variant="strong-sm" color={Colors.Neutral700} className={styles.columnField}>
              Field
            </Text>
            <Text variant="strong-sm" color={Colors.Neutral700} className={styles.columnIssue}>
              Issue
            </Text>
            <Text
              variant="strong-sm"
              color={Colors.Neutral700}
              className={styles.columnSeverity}
            >
              Severity
            </Text>
          </div>

          {/* Table Rows */}
          {fileData.issues.map((issue, index) => (
            <div key={index} className={styles.tableRow}>
              <Text variant="regular-sm" color={Colors.Neutral900} className={styles.columnRow}>
                {issue.row}
              </Text>
              <Text
                variant="regular-sm"
                color={Colors.Neutral900}
                className={styles.columnField}
              >
                {issue.field}
              </Text>
              <Text
                variant="regular-sm"
                color={Colors.Neutral900}
                className={styles.columnIssue}
              >
                {issue.issue}
              </Text>
              <div className={styles.columnSeverity}>
                <SeverityChip severity={issue.severity} />
              </div>
            </div>
          ))}
        </Flex>
      )}
    </Flex>
  );

  return (
    <Flex flexDirection="column" gap={SPACING.LARGE}>
      {filesData.map(renderFileCard)}
    </Flex>
  );
};

export default FileValidationCard;
